(function () {
  'use strict';

  // Marks a cart line as a gift. Kept variant-based for the discount app.
  const GIFT_PROPERTY = '_gift-id';
  // Identifies which tier a gift line belongs to, so the same variant can be
  // offered by several tiers without the lines colliding.
  const TIER_PROPERTY = '_gift-tier';
  const CHOICE_STORAGE_PREFIX = 'prohemp:giftChoice:';

  // Verbose tracing of the gift flow. Set to false once it is verified in production.
  const DEBUG = true;

  function log(...args) {
    if (DEBUG) console.log('[gift]', ...args);
  }

  class CartGiftManager {
    constructor() {
      this._syncing = false;
    }

    /* ------------------------------ config ------------------------------ */

    /**
     * Normalized tiers from the config script rendered by 'cart-gift-bar'.
     * Shape: { key, thresholdCents, variantIds: [numeric variant ids] }
     */
    getTiers() {
      const el = document.getElementById('gift-config-data');
      if (!el) return [];

      try {
        const parsed = JSON.parse(el.textContent || '[]');
        if (!Array.isArray(parsed)) return [];

        return parsed
          .filter((tier) => tier && tier.enabled && Array.isArray(tier.variants))
          .map((tier) => ({
            key: this.tierKey(tier.threshold),
            thresholdCents: Math.round(tier.threshold * 100),
            variantIds: tier.variants
              .map((variant) => this._gidToNumericId(variant && variant.variantId))
              .filter(Boolean),
          }))
          .filter((tier) => tier.variantIds.length);
      } catch {
        return [];
      }
    }

    tierKey(value) {
      const number = Number(value);
      return Number.isNaN(number) ? String(value) : String(number);
    }

    _gidToNumericId(gid) {
      if (typeof gid === 'number') return gid;
      if (typeof gid !== 'string') return null;
      const id = parseInt(gid.split('/').pop(), 10);
      return Number.isNaN(id) ? null : id;
    }

    /* -------------------------- customer choice -------------------------- */

    _getStoredChoice(tierKey) {
      try {
        const stored = parseInt(sessionStorage.getItem(CHOICE_STORAGE_PREFIX + tierKey), 10);
        return Number.isNaN(stored) ? null : stored;
      } catch {
        return null;
      }
    }

    _setStoredChoice(tierKey, variantId) {
      try {
        sessionStorage.setItem(CHOICE_STORAGE_PREFIX + tierKey, String(variantId));
      } catch {
        /* ignore quota / private mode */
      }
    }

    /* ------------------------------ cart io ------------------------------ */

    async _fetchCart() {
      const res = await fetch('/cart.js');
      if (!res.ok) throw new Error('Failed to fetch cart');
      return res.json();
    }

    _cartTotalExclGifts(cart) {
      return cart.items.reduce((sum, item) => {
        const giftId = item.properties && item.properties[GIFT_PROPERTY];
        return giftId ? sum : sum + item.final_line_price;
      }, 0);
    }

    _giftLines(cart) {
      return cart.items
        .filter((item) => item.properties && item.properties[GIFT_PROPERTY])
        .map((item) => ({
          key: item.key,
          variantId: item.variant_id,
          tierKey: item.properties[TIER_PROPERTY] || null,
        }));
    }

    async _removeLines(keys) {
      const updates = {};
      keys.forEach((key) => (updates[key] = 0));

      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates }),
      });
      if (!res.ok) throw new Error('Failed to remove gift items');
    }

    /** Adds one gift variant for a tier. Resolves false when it can't be added. */
    async _addGiftVariant(tierKey, variantId) {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity: 1,
              properties: {
                [GIFT_PROPERTY]: 'gift-' + variantId,
                [TIER_PROPERTY]: tierKey,
              },
            },
          ],
        }),
      });

      if (!res.ok) {
        console.warn('[CartGiftManager] Gift variant unavailable:', variantId);
        return false;
      }
      return true;
    }

    /** Tries the preferred variant first, then the rest of the tier as fallback. */
    async _addGiftForTier(tier, preferredVariantId) {
      const candidates = tier.variantIds.includes(preferredVariantId)
        ? [preferredVariantId, ...tier.variantIds.filter((id) => id !== preferredVariantId)]
        : tier.variantIds;

      for (const variantId of candidates) {
        if (await this._addGiftVariant(tier.key, variantId)) return true;
      }
      return false;
    }

    /* -------------------------------- sync -------------------------------- */

    /** Brings the gift lines in line with the cart total. Returns true if the cart changed. */
    async sync(cartData = null) {
      if (this._syncing) return false;
      this._syncing = true;

      try {
        const tiers = this.getTiers();
        log('sync: tiers =', tiers);
        if (!tiers.length) {
          log('sync: no usable tiers — check #gift-config-data');
          return false;
        }

        const cart = cartData || (await this._fetchCart());
        const cartTotal = this._cartTotalExclGifts(cart);
        const giftLines = this._giftLines(cart);
        const tierKeys = new Set(tiers.map((tier) => tier.key));

        const toAdd = [];
        const toRemoveKeys = [];
        const claimedKeys = new Set();

        for (const tier of tiers) {
          // If an interrupted swap left two lines on a tier, keep the chosen one.
          const tierLines = giftLines.filter((item) => item.tierKey === tier.key);
          const choice = this._getStoredChoice(tier.key);
          const line = tierLines.find((item) => item.variantId === choice) || tierLines[0];
          const qualifies = cartTotal >= tier.thresholdCents;

          if (line) claimedKeys.add(line.key);

          if (qualifies && !line) {
            toAdd.push(tier);
          } else if (!qualifies && line) {
            toRemoveKeys.push(line.key);
          } else if (qualifies && line && !tier.variantIds.includes(line.variantId)) {
            // Config changed: this gift is no longer offered for the tier.
            toRemoveKeys.push(line.key);
            toAdd.push(tier);
          }
        }

        // Gift lines from a removed tier, a duplicate, or a cart built before
        // tiers existed — none of them belong in the cart any more.
        giftLines.forEach((line) => {
          const orphaned = !line.tierKey || !tierKeys.has(line.tierKey);
          if (orphaned || !claimedKeys.has(line.key)) toRemoveKeys.push(line.key);
        });

        log('sync: cart total (excl. gifts) =', cartTotal, 'gift lines =', giftLines);
        log('sync: to add =', toAdd.map((tier) => tier.key), 'to remove =', toRemoveKeys);

        if (!toAdd.length && !toRemoveKeys.length) return false;

        let changed = false;

        if (toRemoveKeys.length) {
          await this._removeLines([...new Set(toRemoveKeys)]);
          changed = true;
        }

        for (const tier of toAdd) {
          const added = await this._addGiftForTier(tier, this._getStoredChoice(tier.key));
          if (added) changed = true;
        }

        return changed;
      } catch (err) {
        console.error('[CartGiftManager]', err);
        return false;
      } finally {
        this._syncing = false;
      }
    }

    /**
     * Swaps the gift of one tier for another variant it offers.
     * Adds first, so a sold-out pick never leaves the customer without a gift.
     */
    async chooseGift(tierKey, variantId) {
      if (this._syncing) return false;
      this._syncing = true;

      try {
        const tier = this.getTiers().find((item) => item.key === this.tierKey(tierKey));
        if (!tier) return false;

        const numericId = this._gidToNumericId(variantId);
        if (!numericId || !tier.variantIds.includes(numericId)) return false;

        const cart = await this._fetchCart();
        const current = this._giftLines(cart).find((item) => item.tierKey === tier.key);
        if (current && current.variantId === numericId) return false;

        if (!(await this._addGiftVariant(tier.key, numericId))) return false;

        this._setStoredChoice(tier.key, numericId);
        if (current) await this._removeLines([current.key]);

        return true;
      } catch (err) {
        console.error('[CartGiftManager]', err);
        return false;
      } finally {
        this._syncing = false;
      }
    }
  }

  /** Bottom sheet that lets the customer swap the gift of a tier. */
  class CartGiftPicker {
    constructor(manager) {
      this.manager = manager;
      this.openerTier = null;
      this.closeTimer = null;

      // '.cart-drawer-box' calls stopPropagation on every click inside the panel,
      // and '.cart-drawer' closes the drawer on any click that reaches it. Listening
      // on the capture phase runs before both, so the sheet gets its events either way.
      document.addEventListener('click', this._onClick.bind(this), true);
      document.addEventListener('keydown', this._onKeydown.bind(this));
    }

    get root() {
      return document.querySelector('[data-gift-picker]');
    }

    get isOpen() {
      const root = this.root;
      return !!root && root.classList.contains('cart-gift-picker--open');
    }

    _onClick(event) {
      const opener = event.target.closest('.cart-drawer__chose-gift-btn');

      if (opener) {
        log('opener clicked, tier =', opener.dataset.giftTier);
        event.preventDefault();
        event.stopPropagation();
        this.open(opener.dataset.giftTier);
        return;
      }

      // Everything below happens inside the sheet, which sits outside
      // '.cart-drawer-box' — without stopping it the drawer would close.
      const insidePicker = event.target.closest('.cart-gift-picker');
      if (!insidePicker || !this.isOpen) return;

      event.preventDefault();
      event.stopPropagation();

      if (event.target.closest('[data-gift-picker-close]')) {
        log('close requested');
        this.close();
        return;
      }

      const option = event.target.closest('.cart-gift-picker__option');
      if (option) {
        log('option clicked, tier =', option.dataset.giftTier, 'variant =', option.dataset.variantId);
        this.select(option);
      }
    }

    _onKeydown(event) {
      if (event.key === 'Escape' && this.isOpen) this.close();
    }

    open(tierKey) {
      const root = this.root;
      if (!root) {
        console.warn('[CartGiftPicker] No picker markup found — is the cart-gift-picker snippet rendered?');
        return;
      }
      if (!tierKey) {
        console.warn('[CartGiftPicker] Button has no data-gift-tier.');
        return;
      }

      const key = this.manager.tierKey(tierKey);
      const lists = root.querySelectorAll('.cart-gift-picker__list');
      log('opening tier', key, '—', lists.length, 'list(s) rendered');
      let matched = false;

      lists.forEach((list) => {
        const isMatch = this.manager.tierKey(list.dataset.giftTier) === key;
        list.hidden = !isMatch;
        if (isMatch) matched = true;
      });

      if (!matched) {
        console.warn(
          '[CartGiftPicker] No gift options rendered for tier ' + key + '. Rendered tiers: ' +
            (Array.from(lists).map((list) => list.dataset.giftTier).join(', ') || 'none') +
            '. Check that every variant in the metaobject has a productHandle.'
        );
        return;
      }

      window.clearTimeout(this.closeTimer);
      this.openerTier = String(tierKey);
      root.hidden = false;

      // Let the browser paint the hidden state before transitioning it in.
      requestAnimationFrame(() => root.classList.add('cart-gift-picker--open'));

      log('sheet open');

      const checked = root.querySelector('.cart-gift-picker__option[aria-checked="true"]:not([disabled])');
      const first = root.querySelector('.cart-gift-picker__option:not([disabled])');
      (checked || first)?.focus();
    }

    close({ focusOpener = true } = {}) {
      const root = this.root;
      if (!root) return;

      root.classList.remove('cart-gift-picker--open');
      this.closeTimer = window.setTimeout(() => {
        const current = this.root;
        if (current && !current.classList.contains('cart-gift-picker--open')) current.hidden = true;
      }, 250);

      if (focusOpener) this.focusOpener();
      this.openerTier = null;
    }

    /** The drawer re-renders on every cart change, so the button is looked up fresh. */
    focusOpener() {
      if (!this.openerTier) return;
      const opener = document.querySelector(
        `.cart-drawer__chose-gift-btn[data-gift-tier="${this.openerTier}"]`
      );
      opener?.focus();
    }

    async select(option) {
      const root = this.root;
      if (!root || option.disabled) return;

      const { giftTier, variantId } = option.dataset;
      root.classList.add('cart-gift-picker--busy');

      try {
        const changed = await this.manager.chooseGift(giftTier, variantId);
        log('chooseGift ->', changed);
        this.close({ focusOpener: !changed });
        if (changed) {
          await window.updateCartDrawer?.();
          this.openerTier = String(giftTier);
          this.focusOpener();
          this.openerTier = null;
        }
      } finally {
        this.root?.classList.remove('cart-gift-picker--busy');
      }
    }
  }

  window.CartGiftManager = new CartGiftManager();
  window.CartGiftPicker = new CartGiftPicker(window.CartGiftManager);
})();
